import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./MatchCard.css";
import { adjustBrightness } from "../Functions/Colors";
import { STATUS_LABELS } from "../Functions/matchStatus";
import { useSiteSettings } from "../siteSettings";
import MatchDetails from "./MatchDetails";
import TeamLogo from "./TeamLogo";
import { KitColorSwatches } from "./KitColorsEditor";
import "./KitColors.css";

const TBD = "Por definir";

function MatchCard({
  displayLeague = false,
  team1,
  team1Logo,
  team1KitColors,
  team1Score,
  team2,
  team2Logo,
  team2KitColors,
  team2Score,
  time,
  date,
  status,
  gameID,
  league,
  leagueName,
  roundLabel,
  themeColor: parentThemeColor,
  accentColor,
}) {
  const { accentColor: siteAccent } = useSiteSettings();
  const themeColor = parentThemeColor || siteAccent;
  const [displayStats, setDisplayStats] = useState(false);

  const secondaryColor = adjustBrightness(themeColor, 0.45);
  const stripColor = accentColor || themeColor;
  const leaguePath = league ? `/ligas/${league}` : null;

  const isPlayed = status === "played";
  const isOff = status === "cancelled" || status === "postponed";
  const s1 = Number(team1Score) || 0;
  const s2 = Number(team2Score) || 0;
  const outcome1 = isPlayed ? (s1 > s2 ? "win" : s1 < s2 ? "loss" : "draw") : null;
  const outcome2 = isPlayed ? (s2 > s1 ? "win" : s2 < s1 ? "loss" : "draw") : null;

  const name1 = team1 || TBD;
  const name2 = team2 || TBD;

  const summary = isPlayed
    ? `${name1} ${s1}, ${name2} ${s2}. ${STATUS_LABELS.played}`
    : `${name1} contra ${name2}. ${
        isOff ? STATUS_LABELS[status] : `${date || ""} ${time || ""}`.trim()
      }`;

  const toggleStats = () => setDisplayStats((open) => !open);

  return (
    <div
      className={`match-card-wrap is-${status || "unplayed"}${
        displayStats ? " expanded" : ""
      }`}
      style={{
        "--theme-color": themeColor,
        "--secondary-color": secondaryColor,
        "--match-accent": stripColor,
      }}
    >
      <div className="match-card">
        <button
          type="button"
          className="match-card-main"
          aria-expanded={displayStats}
          aria-label={`${summary}. ${
            displayStats ? "Ocultar goleadores" : "Ver goleadores"
          }`}
          onClick={toggleStats}
        >
          <TeamRow
            team={name1}
            isTbd={!team1}
            logoUrl={team1Logo}
            kitColors={team1KitColors}
            score={team1Score}
            showScore={isPlayed}
            outcome={outcome1}
          />
          <TeamRow
            team={name2}
            isTbd={!team2}
            logoUrl={team2Logo}
            kitColors={team2KitColors}
            score={team2Score}
            showScore={isPlayed}
            outcome={outcome2}
          />
        </button>

        <div className="match-card-meta">
          {displayLeague &&
            (leaguePath ? (
              <Link to={leaguePath} className="match-league match-league-link">
                {leagueName || "Liga"}
              </Link>
            ) : (
              <span className="match-league">{leagueName || "Liga"}</span>
            ))}
          {roundLabel && <span className="match-round">{roundLabel}</span>}

          {/* Mirror of the main toggle for pointer users; the main button is the keyboard target. */}
          <button
            type="button"
            className="match-card-meta-toggle"
            tabIndex={-1}
            aria-hidden="true"
            onClick={toggleStats}
          >
            {isPlayed ? (
              <span className="match-status match-status--final">
                {STATUS_LABELS.played}
              </span>
            ) : isOff ? (
              <span className={`match-status match-status--${status}`}>
                {STATUS_LABELS[status]}
              </span>
            ) : (
              <span className="match-time">{time || "Por definir"}</span>
            )}
            <span className="match-date">{date || "—"}</span>
            <span
              className={`match-expand-hint${displayStats ? " open" : ""}`}
            >
              <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
                <path
                  d="M2.5 4.5 6 8l3.5-3.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <div className={`match-card-details${displayStats ? " open" : ""}`}>
        {displayStats && (
          <MatchDetails
            themeColor={themeColor}
            secondaryColor={secondaryColor}
            gameID={gameID}
            homeTeam={team1}
            awayTeam={team2}
          />
        )}
      </div>
    </div>
  );
}

function TeamRow({ team, isTbd, logoUrl, kitColors, score, showScore, outcome }) {
  return (
    <div className={`match-team-row${outcome ? ` is-${outcome}` : ""}`}>
      <span className="match-team-identity">
        <TeamLogo name={isTbd ? "" : team} logoUrl={logoUrl} size="sm" />
        <span className={`match-team-name${isTbd ? " is-tbd" : ""}`}>{team}</span>
        <KitColorSwatches colors={kitColors} size={12} />
      </span>
      <span className={`match-score${showScore ? " played" : " unplayed"}`}>
        {showScore ? score ?? 0 : "–"}
      </span>
    </div>
  );
}

export default MatchCard;
